

import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import useManagePatient from "../hooks/patientDashboard/useManagePatient";
import useProfileStore from "../store/patient/profileStore";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const {findPatient} = useManagePatient()
  const {profile, saveProfile} = useProfileStore()

  // API base URL - adjust according to your environment
  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

  // Get user data from API
  const getUser = async (token) => {
   findPatient()
      
  };

  // Verify token and get user data
  const verifyToken = async (token) => {
    try {
      setLoading(true);
      const userData = await getUser(token);
      
      if (profile) {
        const completeUserData = {
          ...userData,
          token: token,
          timestamp: new Date().toISOString()
        };
        
        setUser(completeUserData);
        localStorage.setItem("user", JSON.stringify(completeUserData));
        return completeUserData;
      }
      return null;
    } catch (error) {
      // If token is invalid, clear storage
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Enhanced login function
  const login = async (userData, token) => {
    try {
      setLoading(true);
      setError(null);

      const resolvedToken = token || userData?.token || localStorage.getItem("token");
      const completeUserData = {
        ...(typeof userData === "object" ? userData : {}),
        token: resolvedToken,
        timestamp: userData?.timestamp || new Date().toISOString(),
      };

      setUser(completeUserData);
      localStorage.setItem("user", JSON.stringify(completeUserData));
      if (resolvedToken) {
        localStorage.setItem("token", resolvedToken);
      }
      return completeUserData;
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Enhanced logout function
  const logout = () => {
    setUser(null);
    setError(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("tokenV2");
    localStorage.removeItem("userId");
  };

  // Check if token is expired
  const isTokenExpired = (userData) => {
    if (!userData) return true;

    // Check JWT exp claim if present
    const token = userData.token || (typeof userData === "string" ? userData : null);
    if (token && typeof token === "string" && token.includes(".")) {
      try {
        const payloadBase64 = token.split(".")[1];
        const payload = JSON.parse(atob(payloadBase64));
        if (payload.exp) {
          return Date.now() >= payload.exp * 1000;
        }
      } catch (e) {
        // ignore JWT decoding error
      }
    }

    // Fall back to client timestamp if present (48h lifetime)
    if (userData.timestamp) {
      const tokenTime = new Date(userData.timestamp);
      const currentTime = new Date();
      const hoursDiff = (currentTime - tokenTime) / (1000 * 60 * 60);
      return hoursDiff > 48;
    }

    return false;
  };

  // Refresh user data
  const refreshUser = async () => {
    if (profile?.token) {
      try {
        await verifyToken(user.token);
      } catch (error) {
        console.error('Failed to refresh user data:', error);
      }
    }
  };

  // Check authentication status on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const storedUser = localStorage.getItem("user");

        if (storedUser && storedUser !== "null" && storedUser !== "undefined") {
          const parsedUser = JSON.parse(storedUser);

          if (!parsedUser || isTokenExpired(parsedUser)) {
            console.log('Token expired, logging out...');
            logout();
            return;
          }

          setUser(parsedUser);
        } else {
          setUser(null);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        setUser(null);
        localStorage.removeItem("user");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Auto-refresh user data periodically (optional)
  useEffect(() => {
    if (user?.token) {
      const refreshInterval = setInterval(() => {
        refreshUser();
      }, 30 * 60 * 1000); // Refresh every 30 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [user?.token]);

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!user && !isTokenExpired(user);
  };

  // Check user role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Get user permissions (extend based on your user model)
  const getPermissions = () => {
    return user?.permissions || [];
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    refreshUser,
    isAuthenticated,
    hasRole,
    hasAnyRole,
    getPermissions,
    verifyToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Higher Order Component for protecting routes
export const withAuth = (Component) => {
  return (props) => {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
      return <div>Loading...</div>; // Or your custom loading component
    }
    
    if (!isAuthenticated) {
      // Redirect to login or show access denied
      return <div>Access Denied. Please log in.</div>;
    }
    
    return <Component {...props} />;
  };
};

// Hook for protecting specific components
export const useRequireAuth = () => {
  const { isAuthenticated, loading } = useAuth();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      // Redirect to login page
      window.location.href = '/login';
    }
  }, [isAuthenticated, loading]);
  
  return { isAuthenticated, loading };
};